<template>
  <div
    v-if="isVisible"
    class="layer-host"
    :data-layer-depth="layerDepth"
    :style="rootStyle">

    <!-- Per-layer mask scoped to the parent layer's exposed peek strip.
         Each LayerHost owns its own mask sized to the band of parent
         visible to the LEFT of this drawer body — so dimming doesn't
         stack viewport-wide across ancestors. The mask is its own click
         target: clicking it pushes this layer's parent path. Cascade
         multi-layer jumps work for free — pushing /utility/623 from L4
         automatically closes L2, L3, L4 because their routes no longer
         match the new URL. -->
    <div
      class="layer-host__mask"
      :style="maskStyle"
      aria-hidden="true"
      @click.stop="onMaskClick"></div>

    <transition name="layer-host-slide" appear>
      <aside
        class="layer-host__drawer"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel || ('Layer ' + layerDepth)"
        :style="bodyStyle">

        <button
          type="button"
          class="layer-host__close"
          aria-label="Close"
          @click="beforeClose">
          <i class="el-icon-close"></i>
        </button>

        <div
          v-if="layerDepth >= 2 && breadcrumb.length"
          class="layer-host__breadcrumb"
          aria-label="Layer breadcrumb">
          <span
            v-for="(segment, i) in breadcrumb"
            :key="segment.depth">
            <a
              href="#"
              @click.prevent="onBreadcrumbClick(segment)">{{ segment.label }}</a>
            <span v-if="i < breadcrumb.length - 1" class="layer-host__breadcrumb-sep"> › </span>
          </span>
        </div>

        <slot><router-view /></slot>

        <div class="layer-host__live-region" role="status" aria-live="polite" aria-atomic="true"></div>

      </aside>
    </transition>

  </div>
</template>

<script>
// Width sequence per cascade-modal spec — outermost widest, deeper layers
// step inwards in 8% increments. Clamped to depth 4; deeper routes use
// router.replace (see lib/layerNav.js pushLayer).
const DESKTOP_WIDTH_PCT = { 1: 92, 2: 84, 3: 76, 4: 68 }
const MOBILE_FULLSCREEN_MQ = new Set(['xs', 'sm', 'md'])

// Body-scroll lock: when any LayerHost becomes visible, freeze the page
// behind it so wheel/touch scroll on the mask doesn't move the underlying
// content. Restore the original overflow when the last visible layer closes.
// Module-level so nested layers share one counter — L1+L2 visible should
// still leave a single lock, not two competing toggles.
let _visibleLayerCount = 0
let _originalBodyOverflow = null
function _lockBodyScroll () {
  if (_visibleLayerCount === 0) {
    _originalBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  _visibleLayerCount += 1
}
function _unlockBodyScroll () {
  _visibleLayerCount = Math.max(0, _visibleLayerCount - 1)
  if (_visibleLayerCount === 0) {
    document.body.style.overflow = _originalBodyOverflow || ''
    _originalBodyOverflow = null
  }
}
// Light per-layer dim — matches the spec's 0.18 (mocked in
// docs/UX-architecture/mocks/XOL-209-cascade-modal-demo.html as a v-modal
// override). Low opacity keeps the cascade legible when 4 layers stack.
// Actual opacity values live in the .layer-host__mask CSS rule below so
// the :hover state can darken without inline styles fighting it.

export default {
  name: 'layer-host',

  props: {
    layerDepth: {
      type: Number,
      required: true
    },
    breadcrumb: {
      type: Array,
      default: () => []
    },
    ariaLabel: {
      type: String,
      default: null
    },
    // DrawerStack supplies this to force the layer visible without relying on
    // $route.matched depth. Default false preserves the PR-1 canary behaviour
    // where isVisible is computed from matched length.
    forceVisible: {
      type: Boolean,
      default: false
    },
    // When DrawerStack drives this layer it passes the logical parent location
    // so beforeClose / mask click navigate to the right URL. Default null
    // preserves PR-1 canary behaviour (pathAtDepth fallback used instead).
    parentLocation: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      cascadeBusy: false,
      listenerAttached: false
    }
  },

  computed: {
    isVisible () {
      // When DrawerStack owns this layer it forces visibility — the URL-matched
      // depth check doesn't apply because DrawerStack manages visibility itself.
      if (this.forceVisible) return true
      // Visible iff the route still has a matched segment at or below this depth.
      return this.$route.matched.length > this.parentDepth
    },

    parentDepth () {
      return this.layerDepth - 1
    },

    currentDepth () {
      // When DrawerStack drives this layer the matched-record depth counter
      // doesn't reflect the virtual cascade. Return layerDepth directly so
      // isTopmost works correctly in the DrawerStack context.
      if (this.forceVisible) return this.layerDepth
      return this.$route.matched.filter(r => r.meta && r.meta.layerDepth).length
    },

    isTopmost () {
      return this.layerDepth === this.currentDepth
    },

    isMobile () {
      return MOBILE_FULLSCREEN_MQ.has(this.$mq)
    },

    bodyWidthPct () {
      if (this.isMobile) return 100
      const depth = Math.min(this.layerDepth, 4)
      return DESKTOP_WIDTH_PCT[depth] || 68
    },

    parentBodyWidthPct () {
      // For L1 the parent is the base page (full viewport). For L2+ it's
      // the previous layer's body width.
      if (this.layerDepth === 1) return 100
      if (this.isMobile) return 100
      const depth = Math.min(this.layerDepth - 1, 4)
      return DESKTOP_WIDTH_PCT[depth] || 100
    },

    drawerZIndex () {
      // 3000 base + 10/depth: room for body (z+1) above mask (z) per layer.
      return 3000 + this.layerDepth * 10
    },

    maskLeftPct () {
      // Mask starts at the parent body's LEFT edge.
      return 100 - this.parentBodyWidthPct
    },

    maskWidthPct () {
      // Mask spans from the parent's left edge to this body's left edge —
      // i.e. exactly the strip of the parent exposed to the left of this
      // drawer. (E.g. L1's mask is the 8% page strip; L2's mask is the 8%
      // L1 peek; etc.)
      return this.parentBodyWidthPct - this.bodyWidthPct
    },

    rootStyle () {
      // Pin the wrapper to the viewport so child percent units resolve
      // against the viewport. Itself transparent — only mask + body paint.
      return {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: this.drawerZIndex
      }
    },

    maskStyle () {
      // Background/cursor/transition come from the .layer-host__mask CSS rule
      // so the :hover selector can darken on mouse-over (inline styles would
      // block the :hover state).
      return {
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: this.maskLeftPct + '%',
        width: this.maskWidthPct + '%',
        pointerEvents: 'auto',
        zIndex: this.drawerZIndex
      }
    },

    bodyStyle () {
      return {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: this.bodyWidthPct + '%',
        background: '#fff',
        boxShadow: '-8px 0 16px rgba(0, 0, 0, 0.10)',
        pointerEvents: 'auto',
        zIndex: this.drawerZIndex + 1,
        overflowY: 'auto',
        // Breathing room above slot content so the consumer's first row
        // (filter-bar title, etc.) doesn't crowd the absolute-positioned
        // X close button at top: 12px.
        paddingTop: '40px'
      }
    },

    computedParentLocation () {
      // Prop wins (DrawerStack supplies it); else fall back to the route-matched
      // path resolution for the PR-1 canary.
      if (this.parentLocation) return this.parentLocation
      return { path: this.pathAtDepth(this.parentDepth) }
    }
  },

  watch: {
    isTopmost: {
      handler (now) {
        this.syncListener(now)
      },
      immediate: true
    },
    isVisible: {
      handler (now, was) {
        // Body-scroll lock follows visibility, not mount. The L1 LayerHost
        // stays mounted while a child L2 is open; both contribute one lock
        // each so the underlying page stays frozen until everything closes.
        if (now && !was) _lockBodyScroll()
        else if (!now && was) _unlockBodyScroll()
      },
      immediate: true
    }
  },

  methods: {
    pathAtDepth (targetDepth) {
      // Resolve the URL for the layer at `targetDepth` against the current
      // matched chain. depth=0 → the L0 base page (the record one slot
      // above the first LayerHost — e.g. /utility); depth=N → the
      // LayerHost record whose meta.layerDepth === N.
      //
      // NOTE: a naive "deepest non-layerDepth record" search hits the index
      // child of the L1 LayerHost (whose path resolves to the L1 path
      // again), not the L0 page. Anchor against the first LayerHost instead.
      const matched = this.$route.matched
      let record = null
      if (targetDepth <= 0) {
        const firstLayerIndex = matched.findIndex(r => r.meta && r.meta.layerDepth === 1)
        if (firstLayerIndex > 0) {
          record = matched[firstLayerIndex - 1]
        } else if (matched.length > 0) {
          record = matched[0]
        }
      } else {
        record = matched.find(r => r.meta && r.meta.layerDepth === targetDepth)
      }
      if (!record || !record.path) return '/'
      const resolved = record.path.replace(
        /:([^/]+?)(\([^)]+\))?(?=\/|$)/g,
        (_, name) => this.$route.params[name] || ''
      )
      return resolved || '/'
    },

    onMaskClick () {
      // Clicking this layer's mask = clicking the parent's visible peek
      // strip. Push the parent path; any deeper layers that were also
      // open close automatically because their routes won't match.
      if (this.cascadeBusy) return
      this.beforeClose()
    },

    beforeClose () {
      // Forward-only close: push the parent path. Browser back returns the
      // user to the deeper route they closed out of.
      this.$router.push(this.computedParentLocation)
    },

    onBreadcrumbClick (segment) {
      this.$router.push({ path: this.pathAtDepth(segment.depth) })
    },

    onKeyDown (event) {
      // Only the topmost layer reacts to Escape — same gesture as the X
      // button: push parent path, browser back restores. Accept both
      // 'Escape' (modern) and 'Esc' (IE/older Edge) for KeyboardEvent.key.
      const isEsc = event.key === 'Escape' || event.key === 'Esc'
      if (isEsc && this.isTopmost && !this.cascadeBusy) {
        event.preventDefault()
        this.beforeClose()
      }
    },

    syncListener (shouldBeAttached) {
      // ESC keydown is the only window-level listener — cascade pop runs
      // via per-mask @click bindings now, no global mousedown hit-test.
      if (shouldBeAttached && !this.listenerAttached) {
        window.addEventListener('keydown', this._boundKeyDown)
        this.listenerAttached = true
      } else if (!shouldBeAttached && this.listenerAttached) {
        window.removeEventListener('keydown', this._boundKeyDown)
        this.listenerAttached = false
      }
    }
  },

  created () {
    this._boundKeyDown = this.onKeyDown.bind(this)
  },

  mounted () {
    // cascadeBusy guard: suppress overlapping cascade-pop clicks while a
    // route transition is in flight.
    this._routerBefore = this.$router.beforeEach((to, from, next) => {
      this.cascadeBusy = true
      next()
    })
    this._afterEachTimeout = null
    this.$watch('$route', () => {
      clearTimeout(this._afterEachTimeout)
      this._afterEachTimeout = setTimeout(() => {
        this.cascadeBusy = false
      }, 250)
    })

    this.syncListener(this.isTopmost)
  },

  beforeDestroy () {
    this.syncListener(false)
    // If this layer is being torn down while still visible (route swap mid-
    // open, hot reload, etc.) the isVisible watcher won't fire — release the
    // lock here so we don't leak `body { overflow: hidden }`.
    if (this.isVisible) _unlockBodyScroll()
    if (this._routerBefore) {
      this._routerBefore()
    }
    clearTimeout(this._afterEachTimeout)
  }
}
</script>

<style scoped>
/* Per-layer mask. Light dim by default; hover advertises the click target
   per the cascade-modal mock's `.peek-hover-zone` design. Position, sizing,
   and z-index are applied inline via :style — only paint properties live
   here so :hover can take effect. */
.layer-host__mask {
  background: rgba(0, 0, 0, 0.18);
  cursor: pointer;
  transition: background 120ms ease;
}

.layer-host__mask:hover {
  background: rgba(0, 0, 0, 0.28);
}

/* Close button — floats over the drawer content, top-right corner. */
.layer-host__close {
  position: absolute;
  top: 12px;
  right: 16px;
  background: transparent;
  border: 0;
  padding: 6px;
  font-size: 22px;
  line-height: 1;
  color: #909399;
  cursor: pointer;
  border-radius: 4px;
  z-index: 1;
}

.layer-host__close:hover {
  color: #303133;
  background: #f5f7fa;
}

.layer-host__breadcrumb {
  font-size: 12px;
  color: #909399;
  padding: 14px 56px 0 20px;
}

.layer-host__breadcrumb a {
  color: #409eff;
  text-decoration: none;
  cursor: pointer;
}

.layer-host__breadcrumb a:hover {
  text-decoration: underline;
}

.layer-host__breadcrumb-sep {
  margin: 0 4px;
  color: #c0c4cc;
}

.layer-host__live-region {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

/* Slide-from-right open transition */
.layer-host-slide-enter-active,
.layer-host-slide-leave-active {
  transition: transform 0.3s ease;
}

.layer-host-slide-enter,
.layer-host-slide-leave-to {
  transform: translateX(100%);
}
</style>
